import classnames from 'classnames';
import React, { useEffect, useRef } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { CloseButton } from "./CloseButton.js";
import { ModalProvider } from "./provider/ModalProvider.js";
import { Backdrop } from "../backdrop/Backdrop.js";
import { Portal } from "../portal/Portal.js";
import { Progress } from "../progress/Progress.js";
import { Typography } from "../typography/Typography.js";
import styles from "./Modal.module.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const usePrevious = value => {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};
const useBackdropRef = ref => {
  const internalRef = useRef(null);
  return ref ?? internalRef;
};
export const Modal = ({
  overline,
  title,
  children,
  className,
  closeable,
  onOpen,
  onClose,
  progress,
  close,
  onEsc,
  closeOnEsc,
  onOverlayClick,
  closeOnOverlayClick,
  initialFocusRef,
  open = true,
  id,
  backdropRef,
  customProgress,
  noClip = false,
  role,
  size = 'M',
  'data-testid': dataTestId,
  refDocument = typeof window !== 'undefined' ? window.document : undefined
}) => {
  const modalRef = useRef(null);
  const previousOpen = usePrevious(open);
  const ref = useBackdropRef(backdropRef);

  /**
   * calls onOpen
   *
   * runs whenever
   * - `onOpen` is present
   * - `open` changed to true
   */
  useEffect(() => {
    if (onOpen && open && previousOpen !== open) {
      onOpen();
    }
  }, [onOpen, open, previousOpen]);

  /**
   * calls onClose
   *
   * runs whenever
   * - `onClose` is present
   * - `open` changed to false
   */
  useEffect(() => {
    if (onClose && !open && previousOpen !== open) {
      onClose();
    }
  }, [onClose, open, previousOpen]);

  /**
   * adds an eventListener for keydown when open, watching for `Escape`
   *
   * runs whenever:
   * - `open` is true
   * - `onEsc` or `close` exists
   */
  useEffect(() => {
    if (open && (onEsc || close)) {
      const escapeListener = event => {
        if (event.key === 'Escape') {
          if (onEsc) {
            onEsc();
          }
          if (closeable && closeOnEsc && close) {
            close();
          }
        }
      };
      refDocument?.addEventListener('keydown', escapeListener);
      return () => {
        refDocument?.removeEventListener('keydown', escapeListener);
      };
    }
  }, [closeOnEsc, close, closeable, open, onEsc, refDocument]);

  /**
   * focuses given element via `initialFocusRef`
   *
   * runs whenever
   * - `open` is true
   * - `open` changed to true`
   * - an `initialFocusRef` was given
   *
   */
  useEffect(() => {
    if (open && previousOpen !== open && initialFocusRef?.current) {
      initialFocusRef.current.focus();
    }
  }, [initialFocusRef, open, previousOpen]);
  if (!open) {
    return null;
  }
  const proxyOnOverlayClick = ({
    clientX,
    clientY,
    target
  }) => {
    const hasOnOverlayClick = !!onOverlayClick;
    const canClose = closeable && close && closeOnOverlayClick;
    if (hasOnOverlayClick || canClose) {
      // initialized as 0 to enable testing `onOverlayClick` - else both values are undefined
      const {
        x = 0,
        y = 0,
        width = 0,
        height = 0
      } = modalRef.current?.getBoundingClientRect() ?? {};
      const isXOutside = clientX < x || clientX > x + width;
      const isYOutside = clientY < y || clientY > y + height;
      const clickedScrollbar = clientX > target.clientWidth;
      const clickedOverlayDirectly = target === ref.current;
      const isOutside = isXOutside || isYOutside;
      if (!isOutside || clickedScrollbar || !clickedOverlayDirectly) {
        return;
      }
      if (hasOnOverlayClick) {
        onOverlayClick();
      }
      if (canClose) {
        close();
      }
    }
  };
  const portalClasses = classnames(styles.modal, className);
  const headerClasses = classnames(styles.header, {
    [styles.closeable]: closeable
  });
  const hasProgress = typeof progress === 'number';
  return /*#__PURE__*/_jsx(Portal, {
    id: id,
    className: portalClasses,
    refDocument: refDocument,
    children: /*#__PURE__*/_jsx(Backdrop, {
      onMouseDown: proxyOnOverlayClick,
      className: styles.backdrop,
      "data-testid": `${dataTestId}-backdrop`,
      children: /*#__PURE__*/_jsx(RemoveScroll, {
        className: styles.scrollLock,
        children: /*#__PURE__*/_jsx(ModalProvider, {
          children: /*#__PURE__*/_jsx("div", {
            ref: ref,
            className: styles.portalContainer,
            children: /*#__PURE__*/_jsxs("article", {
              className: classnames(styles.body, {
                [styles.noClip]: noClip
              }, styles[size]),
              ref: modalRef,
              role: role,
              "data-testid": dataTestId,
              children: [closeable && /*#__PURE__*/_jsx(CloseButton, {
                handleClose: close,
                skipButtonFocus: !!initialFocusRef
              }), /*#__PURE__*/_jsxs("header", {
                className: headerClasses,
                children: [overline && /*#__PURE__*/_jsx(Typography, {
                  className: styles.overline,
                  margin: false,
                  children: overline
                }), title && /*#__PURE__*/_jsx(Typography, {
                  variant: size === 'S' ? 'h3' : 'h2',
                  id: `${id}-label`,
                  margin: false,
                  children: title
                }), hasProgress && /*#__PURE__*/_jsx(Progress, {
                  value: progress,
                  className: styles.progress
                }), customProgress]
              }), /*#__PURE__*/_jsx("div", {
                className: styles.scrollingContent,
                children: children
              })]
            })
          })
        })
      })
    })
  });
};